import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import './LicensingPage.css';

const LicensingPage: React.FC = () => {
  const { t } = useTranslation();
  usePageTitle('Licensing - Platform, API & Enterprise Options');

  const licensees = t('licensing.whoLicenses.items', { returnObjects: true }) as string[];

  const licensingModels = ['platform', 'api', 'enterprise'];

  const capabilities = [
    'caseLogic',
    'venueIQ',
    'syntheticPanel',
    'trialSim',
    'expertWitness',
    'deliberation',
    'riskView'
  ];

  const integrations = [
    { key: 'api' },
    { key: 'whiteLabel' },
    { key: 'partner' }
  ];

  return (
    <div className="licensing-page">
      {/* Hero Section */}
      <section className="licensing-hero">
        <div className="container">
          <span className="section-label">{t('licensing.label')}</span>
          <h1>{t('licensing.title')}</h1>
          <p className="licensing-subtitle">
            {t('licensing.subtitle')}
          </p>
          <p className="licensing-intro">
            {t('licensing.intro')}
          </p>
        </div>
      </section>

      {/* Who Licenses Section */}
      <section className="who-licenses">
        <div className="container">
          <div className="section-header">
            <h2>{t('licensing.whoLicenses.title')}</h2>
            <p className="section-description">{t('licensing.whoLicenses.description')}</p>
          </div>
          <div className="licensees-list">
            {licensees.map((licensee, index) => (
              <div key={index} className="licensee-item">
                <span className="licensee-bullet"></span>
                {licensee}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Licensing Models Section */}
      <section className="licensing-models">
        <div className="container">
          <div className="section-header">
            <h2>{t('licensing.models.title')}</h2>
            <p className="section-subtitle">{t('licensing.models.subtitle')}</p>
          </div>
          <div className="models-grid">
            {licensingModels.map((model) => {
              const features = t(`licensing.models.${model}.features`, { returnObjects: true }) as string[];
              return (
                <div key={model} className={`model-card model-${model}`}>
                  <h3>{t(`licensing.models.${model}.title`)}</h3>
                  <p className="model-description">
                    {t(`licensing.models.${model}.description`)}
                  </p>
                  <ul className="model-features">
                    {features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Core Capabilities Section */}
      <section className="core-capabilities">
        <div className="container">
          <div className="section-header">
            <h2>{t('licensing.capabilities.title')}</h2>
            <p className="section-subtitle">{t('licensing.capabilities.subtitle')}</p>
          </div>
          <div className="capabilities-grid">
            {capabilities.map((cap) => (
              <div key={cap} className="capability-card">
                <h4>{t(`licensing.capabilities.items.${cap}.name`)}</h4>
                <p>{t(`licensing.capabilities.items.${cap}.description`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Options Section */}
      <section className="integration-options">
        <div className="container">
          <div className="section-header">
            <h2>{t('licensing.integration.title')}</h2>
          </div>
          <div className="integrations-grid">
            {integrations.map((integration) => (
              <div key={integration.key} className="integration-card">
                <h3>{t(`licensing.integration.${integration.key}.title`)}</h3>
                <p>{t(`licensing.integration.${integration.key}.description`)}</p>
                <span className="integration-card-rule" aria-hidden="true" />
              </div>
            ))}
          </div>

          <div className="integration-options-footer">
            <Link to="/integrations" className="integration-options-cta">
              View All Integrations
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="licensing-cta">
        <div className="container">
          <div className="cta-content">
            <h2>{t('common.requestDocumentation')}</h2>
            <p>
              {t('contact.subtitle')}
            </p>
            <Link to="/contact?type=docs" className="btn btn-primary">{t('common.requestDocumentation')}</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LicensingPage;
